import type {
    ReactNode
} from "react";
import {
    type PressableStateCallbackType,
    StyleSheet,
    Pressable,
    View
} from "react-native";
import {
    getSizePrefixes,
    formatValue
} from "./layout";
import {
    type AnatomyMeasurement,
    getParentInnerRect,
    type AnatomyPart
} from "./measure";
import {
    type AnatomyColorRole,
    findElevationToken,
    findTypographyRole,
    formatTokenNames,
    findColorToken,
    findSizeTokens,
    isFullRadius
} from "./tokens";
import {
    NCoreUIKitTheme,
    getCornerStyle,
    Text
} from "ncore-ui-kit";

type AnatomySpec = {
    swatch?: string;
    label: string;
    value: string;
    key: string;
};

type Translate = (key: string, args?: Array<string | number>) => string;

const MAX_TEXT_LENGTH = 28;

const withTokens = (value: string, tokens: Array<string>) => {
    const names = formatTokenNames(tokens);

    return names ? `${value} · ${names}` : value;
};

export const getPartKindName = (part: AnatomyPart, t: Translate) => {
    switch(part.kind) {
        case "text":
            return t("anatomy.text");
        case "icon":
            return t("anatomy.icon");
        case "media":
            return t("anatomy.media");
        default:
            return part.childIds.length > 0 ? t("anatomy.container") : t("anatomy.shape");
    }
};

export const getPartName = (part: AnatomyPart, t: Translate, customName?: string) => {
    if(customName) {
        return customName;
    }

    if(part.kind !== "text" || !part.text) {
        return getPartKindName(part, t);
    }

    const text = part.text.length > MAX_TEXT_LENGTH ? `${part.text.slice(0, MAX_TEXT_LENGTH - 1)}…` : part.text;

    return `${t("anatomy.text")} “${text}”`;
};

const getPairedName = (part: AnatomyPart, measurement: AnatomyMeasurement) => {
    const parent = part.parentId === null ? undefined : measurement.parts[part.parentId];

    if(!parent?.background) {
        return undefined;
    }

    const token = findColorToken(parent.background, measurement.themeName, "background");

    return token?.name?.split(".").pop();
};

const getColorSpec = (key: string, label: string, color: string | null, measurement: AnatomyMeasurement, role: AnatomyColorRole, pairedName?: string): AnatomySpec | null => {
    if(!color) {
        return null;
    }

    const token = findColorToken(color, measurement.themeName, role, pairedName);

    if(!token) {
        return null;
    }

    const alpha = token.alpha < 1 ? ` · ${Math.round(token.alpha * 100)}%` : "";

    return {
        value: token.name ?? `${token.hex}${alpha}`,
        swatch: color,
        label,
        key
    };
};

export const getPartDetail = (part: AnatomyPart, measurement: AnatomyMeasurement) => {
    if(part.kind === "text") {
        return part.fontSize !== null && part.fontWeight !== null ? findTypographyRole(part.fontSize, part.lineHeight, part.fontWeight) ?? "" : "";
    }

    if(part.kind === "icon") {
        return findSizeTokens("size", Math.max(part.rect.width, part.rect.height)).find((name) => name.startsWith("iconSizes.")) ?? "";
    }

    if(part.background) {
        return findColorToken(part.background, measurement.themeName, "background")?.name ?? "";
    }

    return part.radius > 0 && !isFullRadius(part.radius, part.rect.width, part.rect.height) ? findSizeTokens("radius", part.radius)[0] ?? "" : "";
};

export const getPartSpecs = (part: AnatomyPart, measurement: AnatomyMeasurement, t: Translate): Array<AnatomySpec> => {
    const specs: Array<AnatomySpec | null> = [];

    if(part.kind === "text" && part.fontSize !== null && part.fontWeight !== null) {
        const role = findTypographyRole(part.fontSize, part.lineHeight, part.fontWeight);

        const metrics = `${formatValue(part.fontSize)}/${part.lineHeight === null ? "–" : formatValue(part.lineHeight)} · ${part.fontWeight}`;

        specs.push({
            value: role ? `${role} · ${metrics}` : metrics,
            label: t("anatomy.typography"),
            key: "typography"
        });
    }

    const sizePrefixes = getSizePrefixes(part);

    const sizeTokens = [
        ...findSizeTokens("size", part.rect.width),
        ...findSizeTokens("size", part.rect.height)
    ].filter((name, index, names) => names.indexOf(name) === index && sizePrefixes.some((prefix) => name.startsWith(`${prefix}.`)));

    if(sizeTokens.length > 0) {
        specs.push({
            value: formatTokenNames(sizeTokens),
            label: t("anatomy.size"),
            key: "size"
        });
    }

    if(part.radius > 0) {
        const isFull = isFullRadius(part.radius, part.rect.width, part.rect.height);

        specs.push({
            value: isFull ? `${t("anatomy.radiusFull")} · radiuses.full` : withTokens(formatValue(part.radius), findSizeTokens("radius", part.radius)),
            label: t("anatomy.radius"),
            key: "radius"
        });
    }

    specs.push(getColorSpec("fill", t("anatomy.fill"), part.background, measurement, "background"));

    if(part.borderWidth > 0) {
        specs.push({
            value: withTokens(formatValue(part.borderWidth), findSizeTokens("border", part.borderWidth)),
            label: t("anatomy.border"),
            key: "borderWidth"
        });

        specs.push(getColorSpec("borderColor", t("anatomy.borderColor"), part.borderColor, measurement, "border"));
    }

    if(part.shadow) {
        specs.push({
            value: findElevationToken(part.shadow) ?? t("anatomy.shadowCustom"),
            label: t("anatomy.shadow"),
            key: "shadow"
        });
    }

    if(part.kind === "text" || part.kind === "icon") {
        specs.push(getColorSpec("color", t("anatomy.color"), part.color, measurement, part.kind, getPairedName(part, measurement)));
    }

    const parent = getParentInnerRect(part, measurement);

    const horizontal = Math.min(part.rect.x - parent.x, parent.x + parent.width - part.rect.x - part.rect.width);

    const vertical = Math.min(part.rect.y - parent.y, parent.y + parent.height - part.rect.y - part.rect.height);

    if(horizontal >= 0.5 || vertical >= 0.5) {
        const insets = [
            horizontal >= 0.5 ? `↔ ${withTokens(formatValue(horizontal), findSizeTokens("space", horizontal))}` : null,
            vertical >= 0.5 ? `↕ ${withTokens(formatValue(vertical), findSizeTokens("space", vertical))}` : null
        ].filter(Boolean);

        specs.push({
            label: t("anatomy.inset"),
            value: insets.join("   "),
            key: "inset"
        });
    }

    return specs.filter((spec): spec is AnatomySpec => spec !== null);
};

export type AnatomyHitArea = {
    token: string | null;
    number: number;
    height: number;
    width: number;
};

const stylesheet = StyleSheet.create({
    number: {
        justifyContent: "center",
        alignSelf: "flex-start",
        alignItems: "center"
    },
    legend: {
        flexDirection: "column"
    },
    part: {
        alignItems: "baseline",
        flexDirection: "row",
        flexWrap: "wrap",
        width: "100%"
    },
    spec: {
        alignItems: "baseline",
        flexDirection: "row",
        maxWidth: "100%",
        flexWrap: "wrap",
        flexShrink: 1,
        minWidth: 0
    },
    specs: {
        flexDirection: "row",
        flexWrap: "wrap",
        flexShrink: 1,
        flexGrow: 2,
        minWidth: 0
    },
    swatch: {
        alignSelf: "center"
    },
    name: {
        flexShrink: 1,
        flexGrow: 1
    },
    value: {
        flexShrink: 1,
        minWidth: 0
    },
    size: {
        flexShrink: 0
    }
});

const AnatomyLegendRow = ({
    onHoverChange,
    isPressed,
    children,
    isActive,
    onPress
}: {
    onHoverChange?: (isHovered: boolean) => void;
    onPress?: () => void;
    isPressed?: boolean;
    children: ReactNode;
    isActive: boolean;
}) => {
    const {
        shapeConfigs,
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const getStyle = (isHighlighted: boolean) => [
        stylesheet.part,
        {
            backgroundColor: isPressed ? colors.content.container.selected : isHighlighted ? colors.content.container.subtle : "transparent",
            paddingHorizontal: spaces.spacingSm + spaces.spacingXxs,
            paddingVertical: spaces.spacingSm,
            ...getCornerStyle({
                radius: radiuses.actions,
                shapeConfigs
            }),
            columnGap: spaces.spacingMd,
            rowGap: spaces.spacingXs
        }
    ];

    if(!onPress) {
        return <View
            onPointerLeave={() => onHoverChange?.(false)}
            onPointerEnter={() => onHoverChange?.(true)}
            style={getStyle(isActive)}
        >
            {children}
        </View>;
    }

    return <Pressable
        style={(state: PressableStateCallbackType & {
            hovered?: boolean;
            focused?: boolean;
        }) => getStyle(isActive || Boolean(state.hovered) || Boolean(state.focused))}
        onHoverOut={() => onHoverChange?.(false)}
        onHoverIn={() => onHoverChange?.(true)}
        onBlur={() => onHoverChange?.(false)}
        onFocus={() => onHoverChange?.(true)}
        aria-pressed={isPressed}
        onPress={onPress}
    >
        {children}
    </Pressable>;
};

const AnatomyLegendNumber = ({
    isActive,
    children
}: {
    children: ReactNode;
    isActive: boolean;
}) => {
    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const size = spaces.spacingMd + spaces.spacingXs;

    return <View
        style={[
            stylesheet.number,
            {
                borderColor: isActive ? colors.content.text.info : colors.content.border.default,
                backgroundColor: isActive ? colors.content.text.info : "transparent",
                borderRadius: radiuses.full,
                borderWidth: borders.line,
                height: size,
                width: size
            }
        ]}
        aria-hidden={true}
    >
        <Text
            color={isActive ? "contrastHigh" : "mid"}
            variant="labelSmallSize"
        >
            {children}
        </Text>
    </View>;
};

const AnatomyLegendSpec = ({
    swatch,
    label,
    value
}: {
    swatch?: string;
    label: string;
    value: string;
}) => {
    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    return <View
        style={[
            stylesheet.spec,
            {
                columnGap: spaces.spacingXs + spaces.spacingXxs
            }
        ]}
    >
        <Text
            variant="bodyMediumSize"
            color="mid"
        >
            {label}
        </Text>
        {
            swatch ?
                <View
                    style={[
                        stylesheet.swatch,
                        {
                            height: spaces.spacingSm + spaces.spacingXxs,
                            width: spaces.spacingSm + spaces.spacingXxs,
                            borderColor: colors.content.border.default,
                            borderRadius: radiuses.xs,
                            borderWidth: borders.line,
                            backgroundColor: swatch
                        }
                    ]}
                    aria-hidden={true}
                />
            :
                null
        }
        <Text
            style={stylesheet.value}
            variant="monoMediumSize"
            color="high"
        >
            {value}
        </Text>
    </View>;
};

const AnatomyLegendBody = ({
    sizeLabel,
    isActive,
    number,
    specs,
    name
}: {
    sizeLabel: string;
    isActive: boolean;
    specs: ReactNode;
    number: number;
    name: string;
}) => {
    const {
        shapeConfigs,
        avatarSizes,
        spaces
    } = NCoreUIKitTheme.useContext();

    return <>
        <AnatomyLegendNumber
            isActive={isActive}
        >
            {number}
        </AnatomyLegendNumber>
        <View
            style={[
                stylesheet.name,
                {
                    flexBasis: avatarSizes.xLarge
                }
            ]}
        >
            <Text
                variant="labelMediumSize"
                color="high"
            >
                {name}
            </Text>
        </View>
        <View
            style={[
                stylesheet.size,
                {
                    width: avatarSizes.xLarge
                }
            ]}
        >
            <Text
                variant="monoMediumSize"
                color="mid"
            >
                {sizeLabel}
            </Text>
        </View>
        <View
            style={[
                stylesheet.specs,
                {
                    flexBasis: shapeConfigs.fieldMinWidth,
                    columnGap: spaces.spacingMd,
                    rowGap: spaces.spacingXxs
                }
            ]}
        >
            {specs}
        </View>
    </>;
};

export const AnatomyLegend = ({
    onHitAreaActiveChange,
    onActivePartChange,
    onPinnedPartChange,
    isHitAreaActive,
    activePartId,
    pinnedPartId,
    measurement,
    hitArea,
    names,
    parts,
    t
}: {
    onActivePartChange: (partId: number | null) => void;
    onPinnedPartChange: (partId: number | null) => void;
    onHitAreaActiveChange: (isActive: boolean) => void;
    measurement: AnatomyMeasurement;
    hitArea: AnatomyHitArea | null;
    activePartId: number | null;
    pinnedPartId: number | null;
    parts: Array<AnatomyPart>;
    isHitAreaActive: boolean;
    names?: Array<string>;
    t: Translate;
}) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    return <View
        style={[
            stylesheet.legend,
            {
                marginTop: spaces.spacingMd,
                rowGap: spaces.spacingXxs
            }
        ]}
        aria-label={t("anatomy.parts")}
        role="list"
    >
        {
            parts.map((part, index) => {
                const isPinned = pinnedPartId === part.id;

                return <View
                    role="listitem"
                    key={part.id}
                >
                    <AnatomyLegendRow
                        onHoverChange={(isHovered) => onActivePartChange(isHovered ? part.id : null)}
                        onPress={() => onPinnedPartChange(isPinned ? null : part.id)}
                        isActive={activePartId === part.id}
                        isPressed={isPinned}
                    >
                        <AnatomyLegendBody
                            sizeLabel={`${formatValue(part.rect.width)} × ${formatValue(part.rect.height)}`}
                            specs={
                                getPartSpecs(part, measurement, t).map((spec) => <AnatomyLegendSpec
                                    swatch={spec.swatch}
                                    label={spec.label}
                                    value={spec.value}
                                    key={spec.key}
                                />)
                            }
                            name={getPartName(part, t, names?.[index])}
                            isActive={activePartId === part.id}
                            number={index + 1}
                        />
                    </AnatomyLegendRow>
                </View>;
            })
        }
        {
            hitArea ?
                <View
                    role="listitem"
                >
                    <AnatomyLegendRow
                        onHoverChange={onHitAreaActiveChange}
                        isActive={isHitAreaActive}
                    >
                        <AnatomyLegendBody
                            sizeLabel={`${formatValue(hitArea.width)} × ${formatValue(hitArea.height)}`}
                            specs={
                                hitArea.token ?
                                    <AnatomyLegendSpec
                                        label={t("anatomy.size")}
                                        value={hitArea.token}
                                    />
                                :
                                    null
                            }
                            name={t("anatomy.hitArea")}
                            isActive={isHitAreaActive}
                            number={hitArea.number}
                        />
                    </AnatomyLegendRow>
                </View>
            :
                null
        }
    </View>;
};
